import { useState, useCallback } from 'react';
import type { SortDir } from './useTableSort';

export type DocLineSortKey = 'product_name' | 'sku' | 'quantity' | 'unit_price' | 'discount_pct' | 'line_total';

interface SortState {
  key: DocLineSortKey;
  dir: SortDir;
}

const heCollator = new Intl.Collator('he', { sensitivity: 'base' });

export function useDocLineSort() {
  const [sort, setSort] = useState<SortState | null>(null);

  const toggle = useCallback((key: DocLineSortKey) => {
    setSort(prev =>
      prev?.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' },
    );
  }, []);

  const reset = useCallback(() => setSort(null), []);

  return { sort, toggle, reset } as const;
}

interface LineBase {
  product_id: string;
  quantity: string;
  unit_price: string;
  discount_pct?: string;
  line_total: number;
}

interface ProductLookup {
  id: string;
  sku: string;
  name: string;
}

export function sortDocLines<L extends LineBase>(
  lines: L[],
  sort: { key: DocLineSortKey; dir: SortDir } | null,
  products: ProductLookup[],
): L[] {
  if (!sort) return lines;

  const prodMap = new Map(products.map(p => [p.id, p]));

  const filled = lines.filter(l => l.product_id);
  const empty = lines.filter(l => !l.product_id);

  const parseNum = (s: string) => { const n = parseFloat(s); return Number.isFinite(n) ? n : 0; };

  const sorted = [...filled].sort((a, b) => {
    let cmp = 0;
    switch (sort.key) {
      case 'product_name': {
        const na = prodMap.get(a.product_id)?.name ?? '';
        const nb = prodMap.get(b.product_id)?.name ?? '';
        cmp = heCollator.compare(na, nb);
        break;
      }
      case 'sku': {
        const sa = prodMap.get(a.product_id)?.sku ?? '';
        const sb = prodMap.get(b.product_id)?.sku ?? '';
        cmp = heCollator.compare(sa, sb);
        break;
      }
      case 'quantity':
        cmp = parseNum(a.quantity) - parseNum(b.quantity);
        break;
      case 'unit_price':
        cmp = parseNum(a.unit_price) - parseNum(b.unit_price);
        break;
      case 'discount_pct':
        cmp = parseNum(a.discount_pct ?? '0') - parseNum(b.discount_pct ?? '0');
        break;
      case 'line_total':
        cmp = a.line_total - b.line_total;
        break;
    }
    return sort.dir === 'desc' ? -cmp : cmp;
  });

  return [...sorted, ...empty];
}
